import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { editDraft } from "@/lib/api";
import { Settings, X } from "lucide-react";

// Convert ISO → datetime-local string in the user's TZ
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

export default function EditSettingsDialog({ open, onOpenChange, draft, hostId }) {
  const [form, setForm] = useState({
    name: draft.name,
    budget: String(draft.budget),
    roster_size: String(draft.roster_size),
    nomination_seconds: String(draft.nomination_seconds),
    bid_extension_seconds: String(draft.bid_extension_seconds),
    nomination_window_seconds: String(draft.nomination_window_seconds || 0),
  });
  const [scheduled, setScheduled] = useState(isoToLocalInput(draft.scheduled_start_at));
  const [saving, setSaving] = useState(false);

  // Reset when opened/closed or draft changes
  useEffect(() => {
    if (open) {
      setForm({
        name: draft.name,
        budget: String(draft.budget),
        roster_size: String(draft.roster_size),
        nomination_seconds: String(draft.nomination_seconds),
        bid_extension_seconds: String(draft.bid_extension_seconds),
        nomination_window_seconds: String(draft.nomination_window_seconds || 0),
      });
      setScheduled(isoToLocalInput(draft.scheduled_start_at));
    }
  }, [open, draft]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const changes = {
        name: form.name,
        budget: Number(form.budget),
        roster_size: Number(form.roster_size),
        nomination_seconds: Number(form.nomination_seconds),
        bid_extension_seconds: Number(form.bid_extension_seconds),
        nomination_window_seconds: Number(form.nomination_window_seconds) || 0,
      };
      if (scheduled) {
        const d = new Date(scheduled);
        if (!isNaN(d.getTime())) changes.scheduled_start_at = d.toISOString();
      } else {
        changes.clear_schedule = true;
      }
      await editDraft(draft.code, hostId, changes);
      toast.success("Settings saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-pbo-cream text-pbo-ink border-pbo-border max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="edit-settings-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-pbo-green text-2xl flex items-center gap-2">
            <Settings className="w-5 h-5 text-pbo-gold" />
            Edit draft settings
          </DialogTitle>
          <p className="text-xs text-pbo-muted">
            Changes apply immediately. Budget changes reset every drafter&apos;s remaining budget.
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <Field id="edit-name" label="Draft name">
            <Input
              id="edit-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="bg-white"
              data-testid="edit-name-input"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field id="edit-budget" label="Budget per team ($)">
              <Input
                id="edit-budget"
                type="number"
                min={10}
                value={form.budget}
                onChange={(e) => set("budget", e.target.value)}
                className="bg-white font-mono"
                data-testid="edit-budget-input"
              />
            </Field>
            <Field id="edit-roster" label="Golfers per team">
              <Input
                id="edit-roster"
                type="number"
                min={1}
                max={20}
                value={form.roster_size}
                onChange={(e) => set("roster_size", e.target.value)}
                className="bg-white font-mono"
                data-testid="edit-roster-input"
              />
            </Field>
            <Field id="edit-nom" label="Initial timer (s)">
              <Input
                id="edit-nom"
                type="number"
                min={5}
                max={120}
                value={form.nomination_seconds}
                onChange={(e) => set("nomination_seconds", e.target.value)}
                className="bg-white font-mono"
                data-testid="edit-nomination-input"
              />
            </Field>
            <Field id="edit-ext" label="Anti-snipe (s)">
              <Input
                id="edit-ext"
                type="number"
                min={3}
                max={30}
                value={form.bid_extension_seconds}
                onChange={(e) => set("bid_extension_seconds", e.target.value)}
                className="bg-white font-mono"
                data-testid="edit-extension-input"
              />
            </Field>
            <Field id="edit-nom-window" label="Nomination limit (s, 0 = off)">
              <Input
                id="edit-nom-window"
                type="number"
                min={0}
                max={600}
                value={form.nomination_window_seconds}
                onChange={(e) => set("nomination_window_seconds", e.target.value)}
                className="bg-white font-mono"
                data-testid="edit-nomination-window-input"
              />
            </Field>
          </div>

          <Field id="edit-sched" label="Scheduled start (leave blank for none)">
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                id="edit-sched"
                type="datetime-local"
                value={scheduled}
                onChange={(e) => setScheduled(e.target.value)}
                className="bg-white font-mono w-auto min-w-[230px]"
                data-testid="edit-scheduled-input"
              />
              {scheduled && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setScheduled("")}
                  className="h-9 text-xs text-pbo-muted hover:text-pbo-green"
                  data-testid="edit-clear-sched-btn"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </Field>
        </div>

        <DialogFooter className="mt-6 gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-pbo-border text-pbo-muted hover:bg-pbo-paper"
            data-testid="edit-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep font-semibold"
            data-testid="edit-save-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label
      htmlFor={id}
      className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted"
    >
      {label}
    </Label>
    {children}
  </div>
);
